import React, { useState, useEffect, useRef, useCallback } from 'react';
import { RefreshCw, Trophy, Flame, Zap, Eraser } from 'lucide-react';

// --- Constants & Config ---
const GRID_SIZE = 6;
const MIN_WORD_LENGTH = 3;
const STONE_CHANCE = 0.1; // 10% chance to spawn a stone
const RAINBOW_CHANCE = 0.05; // 5% chance for a Rainbow Tile

// Fallback Dictionary (in case fetch fails)
const FALLBACK_WORDS = new Set([
    "THE", "AND", "FOR", "ARE", "BUT", "NOT", "YOU", "ALL", "ANY", "CAN", "HAD", "HER", "WAS", "ONE", "OUR", "OUT", "DAY", "GET", "HAS", "HIM", "HIS", "HOW", "MAN", "NEW", "NOW", "OLD", "SEE", "TWO", "WAY", "WHO", "BOY", "DID", "ITS", "LET", "PUT", "SAY", "SHE", "TOO", "USE", "DAD", "MOM", "ACT", "ADD", "AGE", "AIM", "AIR", "ART", "ASK", "BAD", "BAG", "BAR", "BAT", "BED", "BET", "BIG", "BIN", "BIT", "BOX", "BUS", "BUY", "CAP", "CAR", "CAT", "CUP", "CUT", "DOG", "DRY", "EAT", "EGG", "END", "EYE", "FAN", "FAR", "FAT", "FIT", "FIX", "FLY", "FUN", "GAS", "GEM", "GOT", "GUM", "HAT", "HIT", "HOT", "HUG", "ICE", "INK", "JAR", "JET", "JOB", "JOY", "KEY", "KIT", "LAB", "LAW", "LAY", "LIP", "LIT", "LOT", "LOW", "MAP", "MAT", "MIX", "MUD", "NAP", "NET", "NOD", "NUT", "OFF", "OIL", "OWN", "PAN", "PAY", "PEN", "PET", "PIE", "PIN", "POT", "PRO", "RAM", "RAN", "RAP", "RAT", "RED", "RIB", "RID", "RIG", "RIM", "RIP", "ROD", "ROT", "RUB", "RUG", "RUN", "SAD", "SAP", "SAT", "SAW", "SEA", "SET", "SIT", "SKY", "SON", "SUN", "TAG", "TAN", "TAP", "TAX", "TEA", "TEN", "TIE", "TIP", "TOE", "TON", "TOP", "TOY", "TRY", "TUB", "VAN", "VET", "WAR", "WEB", "WED", "WET", "WIN", "WIT", "WON", "YES", "YET", "ZIP", "ZOO", "BARN", "ATE", "FOIL", "EAT", "TEA", "EAR", "ERA", "ART", "TAR", "RAT", "BAT", "TAB", "CAT", "ACT", "HAT", "SAT", "FAT", "MAT", "PAT", "TAP", "MAP", "LAP", "CAP", "NAP", "SAP", "GAP", "RAP", "YAP", "ZAP"
]);

const DICTIONARY_URL = "https://raw.githubusercontent.com/first20hours/google-10000-english/master/google-10000-english-usa-no-swears.txt";

const POOL = "EEEEEEEEEEEEAAAAAAAAAIIIIIIIIIOOOOOOOONNNNNNRRRRRRTTTTTTLLLLSSSSUUUDDDDGGGBBCCMMPPFFHHVVWWYYKJXQZ";
const VOWELS = "AEIOU";

// --- Helper Functions ---
const getRandomCharFromPool = () => POOL[Math.floor(Math.random() * POOL.length)];
const getRandomVowel = () => VOWELS[Math.floor(Math.random() * VOWELS.length)];
const isConsonant = (char) => !VOWELS.includes(char);

const createTile = (id, char, type = 'letter') => ({
    id,
    char,
    type, // 'letter', 'stone', 'rainbow'
    hp: type === 'stone' ? 2 : 1,
    isNew: true, // For animation
    isBlinking: false, // For Thought Blink
    isAboutToBlink: false,
});

const WritersBlockdown = () => {
    const [grid, setGrid] = useState([]);
    const [selection, setSelection] = useState([]); // Array of {r, c}
    const [score, setScore] = useState(0);
    const [message, setMessage] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [level, setLevel] = useState(1);
    const [showWin, setShowWin] = useState(false);
    const [wordList, setWordList] = useState(FALLBACK_WORDS);
    const [isLoadingDictionary, setIsLoadingDictionary] = useState(true);
    const gridRef = useRef(null);
    const selectionRef = useRef([]); // Ref for touch move access
    const columnHistoryRef = useRef(Array(GRID_SIZE).fill([]).map(() => [])); // Track last 2 tile types per col
    const blinkCandidatesRef = useRef([]); // Track tiles selected for blinking
    const timerRef = useRef(0); // Track 30s cycle

    // Initialize Grid & Dictionary
    useEffect(() => {
        initializeGrid();
        fetchDictionary();
    }, []);

    const fetchDictionary = async () => {
        try {
            const response = await fetch(DICTIONARY_URL);
            if (!response.ok) throw new Error("Failed to fetch dictionary");
            const text = await response.text();

            // Process: Split, Filter (3-7 chars), UpperCase
            const words = text.split('\n')
                .map(w => w.trim().toUpperCase())
                .filter(w => w.length >= 3 && w.length <= 7);

            setWordList(new Set(words));
            setIsLoadingDictionary(false);
            console.log("Dictionary loaded:", words.length, "words");
        } catch (error) {
            console.error("Dictionary load failed, using fallback:", error);
            setIsLoadingDictionary(false); // Keep fallback
        }
    };

    // Thought Blink Timer (30s Cycle)
    useEffect(() => {
        const interval = setInterval(() => {
            timerRef.current += 1;

            // T-5s: Warning (25s mark)
            if (timerRef.current === 25) {
                triggerBlinkWarning();
            }

            // T-0s: Swap (30s mark)
            if (timerRef.current >= 30) {
                triggerBlinkSwap();
                timerRef.current = 0;
            }
        }, 1000);
        return () => clearInterval(interval);
    }, []); // No dependencies, uses functional state updates

    const generateChar = (colIndex) => {
        const history = columnHistoryRef.current[colIndex];
        let char;

        // Vowel Insurance: If last 2 were consonants, force vowel
        if (history.length >= 2 && history.every(isConsonant)) {
            char = getRandomVowel();
        } else {
            char = getRandomCharFromPool();
        }

        // Update History
        const newHistory = [...history, char];
        if (newHistory.length > 2) newHistory.shift(); // Keep last 2
        columnHistoryRef.current[colIndex] = newHistory;

        return char;
    };

    const initializeGrid = (resetScore = true) => {
        // Reset history & timer
        columnHistoryRef.current = Array(GRID_SIZE).fill([]).map(() => []);
        timerRef.current = 0;
        blinkCandidatesRef.current = [];

        let newGrid = [];
        let idCounter = 0;
        for (let r = 0; r < GRID_SIZE; r++) {
            let row = [];
            for (let c = 0; c < GRID_SIZE; c++) {
                let type = 'letter';
                if (Math.random() < STONE_CHANCE + (level * 0.01)) type = 'stone';
                else if (Math.random() < RAINBOW_CHANCE) type = 'rainbow';

                const char = (type === 'letter' || type === 'rainbow') ? generateChar(c) : '';
                row.push(createTile(idCounter++, char, type));
            }
            newGrid.push(row);
        }
        setGrid(newGrid);
        if (resetScore) setScore(0);
        setMessage("Swipe to clear blocks!");
        setShowWin(false);
    };

    const triggerBlinkWarning = () => {
        setGrid(prevGrid => {
            const newGrid = prevGrid.map(row => row.map(tile => ({ ...tile })));
            const candidates = [];

            // Find valid candidates (letters only, not already special)
            newGrid.forEach((row, r) => {
                row.forEach((tile, c) => {
                    if (tile && tile.type === 'letter') {
                        candidates.push({ r, c });
                    }
                });
            });

            if (candidates.length < 3) return prevGrid;

            // Pick 3 random
            const selected = [];
            for (let i = 0; i < 3; i++) {
                if (candidates.length === 0) break;
                const idx = Math.floor(Math.random() * candidates.length);
                const { r, c } = candidates.splice(idx, 1)[0];

                newGrid[r][c].isAboutToBlink = true;
                selected.push({ r, c });
            }

            blinkCandidatesRef.current = selected;
            return newGrid;
        });
    };

    const triggerBlinkSwap = () => {
        setGrid(prevGrid => {
            const newGrid = prevGrid.map(row => row.map(tile => ({ ...tile })));
            const candidates = blinkCandidatesRef.current;

            if (candidates.length === 0) return prevGrid;

            candidates.forEach(({ r, c }) => {
                if (newGrid[r][c] && (newGrid[r][c].type === 'letter' || newGrid[r][c].type === 'rainbow')) {
                    // Change char
                    const newChar = getRandomCharFromPool();
                    newGrid[r][c].char = newChar;
                    newGrid[r][c].isAboutToBlink = false; // Clear warning
                    newGrid[r][c].isBlinking = true; // Trigger flash

                    // Small chance to become rainbow if not already
                    if (newGrid[r][c].type === 'letter' && Math.random() < RAINBOW_CHANCE) {
                        newGrid[r][c].type = 'rainbow';
                    }
                }
            });

            // Clear blink flag after animation
            setTimeout(() => {
                setGrid(g => {
                    const gCopy = g.map(row => row.map(t => ({ ...t })));
                    candidates.forEach(({ r, c }) => {
                        if (gCopy[r][c]) gCopy[r][c].isBlinking = false;
                    });
                    return gCopy;
                });
            }, 1000);

            setMessage("New Thoughts...");
            setTimeout(() => setMessage(""), 2000);

            blinkCandidatesRef.current = [];
            return newGrid;
        });
    };

    // --- Input Handling ---
    const getCellFromTouch = (clientX, clientY, strict = false) => {
        if (!gridRef.current) return null;
        const rect = gridRef.current.getBoundingClientRect();
        const x = clientX - rect.left;
        const y = clientY - rect.top;

        // Assuming equal sized cells
        const cellSize = rect.width / GRID_SIZE;
        const c = Math.floor(x / cellSize);
        const r = Math.floor(y / cellSize);

        if (r >= 0 && r < GRID_SIZE && c >= 0 && c < GRID_SIZE) {
            // Strict Mode: Check if touch is within central 70% of cell (15% margin)
            if (strict) {
                const cellX = x % cellSize;
                const cellY = y % cellSize;
                const margin = cellSize * 0.15;
                if (cellX < margin || cellX > cellSize - margin || cellY < margin || cellY > cellSize - margin) {
                    return null; // Ignore touches in the gap/margin
                }
            }
            return { r, c };
        }
        return null;
    };

    const handleStart = (r, c) => {
        if (isProcessing) return;
        if (grid[r][c].type === 'stone') return; // Cannot start on stone

        const newSelection = [{ r, c }];
        setSelection(newSelection);
        selectionRef.current = newSelection;
    };

    const handleMove = (r, c) => {
        if (isProcessing) return;
        const currentSel = selectionRef.current;
        if (currentSel.length === 0) return;

        const last = currentSel[currentSel.length - 1];

        // Check if backtracking (undo selection)
        if (currentSel.length > 1) {
            const prev = currentSel[currentSel.length - 2];
            if (prev.r === r && prev.c === c) {
                const newSel = currentSel.slice(0, -1);
                setSelection(newSel);
                selectionRef.current = newSel;
                return;
            }
        }

        // Interpolation Logic for Fast Swipes
        const dr = r - last.r;
        const dc = c - last.c;
        const dist = Math.max(Math.abs(dr), Math.abs(dc));

        // If moving more than 1 cell, try to fill gaps
        if (dist > 1) {
            const stepR = dr / dist;
            const stepC = dc / dist;

            // Only interpolate if it's a clean line (diagonal or orthogonal)
            // Floating point check: steps should be close to -1, 0, or 1
            const isCleanLine = (Math.abs(stepR) < 0.01 || Math.abs(Math.abs(stepR) - 1) < 0.01) &&
                (Math.abs(stepC) < 0.01 || Math.abs(Math.abs(stepC) - 1) < 0.01);

            if (isCleanLine) {
                let tempSel = [...currentSel];
                let validPath = true;

                for (let i = 1; i <= dist; i++) {
                    const nextR = Math.round(last.r + stepR * i);
                    const nextC = Math.round(last.c + stepC * i);

                    // Check bounds & validity
                    if (nextR < 0 || nextR >= GRID_SIZE || nextC < 0 || nextC >= GRID_SIZE) { validPath = false; break; }
                    if (grid[nextR][nextC].type === 'stone') { validPath = false; break; }
                    if (tempSel.some(s => s.r === nextR && s.c === nextC)) { validPath = false; break; } // Loop detection

                    tempSel.push({ r: nextR, c: nextC });
                }

                if (validPath) {
                    setSelection(tempSel);
                    selectionRef.current = tempSel;
                    return;
                }
            }
        }

        // Standard Adjacency Check (Fallback for slow moves or non-straight fast moves)
        const isAdjacent = Math.abs(dr) <= 1 && Math.abs(dc) <= 1;
        const isSelf = last.r === r && last.c === c;

        if (!isAdjacent || isSelf) return;

        // Check if already selected
        const isAlreadySelected = currentSel.some(s => s.r === r && s.c === c);
        if (isAlreadySelected) return;

        // Check valid type
        if (grid[r][c].type === 'stone') return;

        // Add to selection
        const newSel = [...currentSel, { r, c }];
        setSelection(newSel);
        selectionRef.current = newSel;
    };

    const handleEnd = () => {
        if (isProcessing || selection.length < MIN_WORD_LENGTH) {
            setSelection([]);
            selectionRef.current = [];
            return;
        }

        const word = selection.map(s => grid[s.r][s.c].char).join('');
        validateWord(word, selection);
    };

    // --- Game Logic ---
    const validateWord = (word, selectedCells) => {
        if (wordList.has(word)) {
            // Valid Word!
            processMatch(word, selectedCells);
        } else {
            // Invalid
            setMessage("Unknown Word");
            setSelection([]);
            selectionRef.current = [];

            // Shake animation logic could go here
        }
    };

    const processMatch = async (word, selectedCells) => {
        setIsProcessing(true);
        let points = word.length * 10;
        let newGrid = [...grid.map(row => [...row])]; // Deep copy
        let breakthroughTriggered = false;
        let rainbowTriggered = false;

        // 1. Check for Rainbow usage (Ring Blast)
        const usedRainbow = selectedCells.some(s => newGrid[s.r][s.c].type === 'rainbow');

        if (usedRainbow) {
            rainbowTriggered = true;
            setMessage("MENTAL BREAKTHROUGH!");

            // Identify all tiles to blast (selected + neighbors of rainbow tiles)
            const blastSet = new Set();

            selectedCells.forEach(s => {
                blastSet.add(`${s.r},${s.c}`); // Always remove selected
                if (newGrid[s.r][s.c].type === 'rainbow') {
                    // Add 3x3 neighbors
                    for (let dr = -1; dr <= 1; dr++) {
                        for (let dc = -1; dc <= 1; dc++) {
                            const nr = s.r + dr;
                            const nc = s.c + dc;
                            if (nr >= 0 && nr < GRID_SIZE && nc >= 0 && nc < GRID_SIZE) {
                                blastSet.add(`${nr},${nc}`);
                            }
                        }
                    }
                }
            });

            // Execute Blast
            blastSet.forEach(key => {
                const [r, c] = key.split(',').map(Number);
                if (newGrid[r][c]) {
                    newGrid[r][c] = null; // Destroy everything, including stones
                    points += 50;
                }
            });
            points += 500;
        } else {
            // Normal Match
            setMessage(word + "!");

            // Damage adjacent stones
            selectedCells.forEach(s => {
                // Check 8 neighbors
                for (let dr = -1; dr <= 1; dr++) {
                    for (let dc = -1; dc <= 1; dc++) {
                        const nr = s.r + dr;
                        const nc = s.c + dc;
                        if (nr >= 0 && nr < GRID_SIZE && nc >= 0 && nc < GRID_SIZE) {
                            if (newGrid[nr][nc] && newGrid[nr][nc].type === 'stone') {
                                newGrid[nr][nc].hp -= 1;
                                if (newGrid[nr][nc].hp <= 0) {
                                    newGrid[nr][nc] = null; // Destroy stone
                                    points += 50;
                                }
                            }
                        }
                    }
                }
                // Remove selected tiles
                if (newGrid[s.r][s.c]) newGrid[s.r][s.c] = null;
            });
        }



        setGrid(newGrid);
        setScore(prev => prev + points);
        setSelection([]);
        selectionRef.current = [];

        // Wait for "pop" animation
        setTimeout(() => {
            applyGravity(newGrid);
        }, 300);
    };

    const applyGravity = (currentGrid) => {
        let newGrid = [...currentGrid.map(row => [...row])];
        let stonesRemoved = false;

        // For each column
        for (let c = 0; c < GRID_SIZE; c++) {
            let writeRow = GRID_SIZE - 1;

            // Shift down
            for (let r = GRID_SIZE - 1; r >= 0; r--) {
                if (newGrid[r][c] !== null) {
                    // Fall-off Rule: If it's a stone and it's about to land in the last row (or is there)
                    // Actually, let's simplify: Shift everything down first.
                    // Then check bottom row for stones.

                    // Standard gravity shift
                    newGrid[writeRow][c] = newGrid[r][c];
                    if (writeRow !== r) newGrid[r][c] = null;
                    writeRow--;
                }
            }

            // Fill top
            while (writeRow >= 0) {
                let type = 'letter';
                if (Math.random() < STONE_CHANCE + (level * 0.01)) type = 'stone';
                else if (Math.random() < RAINBOW_CHANCE) type = 'rainbow';

                const char = (type === 'letter' || type === 'rainbow') ? generateChar(c) : '';
                newGrid[writeRow][c] = createTile(Date.now() + Math.random(), char, type);
                writeRow--;
            }
        }

        // Post-Gravity: Check for Stones in Bottom Row (Fall Off)
        for (let c = 0; c < GRID_SIZE; c++) {
            const bottomTile = newGrid[GRID_SIZE - 1][c];
            if (bottomTile && bottomTile.type === 'stone') {
                newGrid[GRID_SIZE - 1][c] = null; // Remove it
                stonesRemoved = true;
                // Trigger refill for this column immediately or next loop? 
                // For simplicity, let's just leave it empty and let next move fill it, 
                // OR recursively call gravity. Recursive is safer for visuals but complex.
                // Let's just remove it. The next move will fill it. 
                // Actually, that leaves a hole. Let's fill it now.

                // Shift column down 1, generate new at top.
                for (let r = GRID_SIZE - 1; r > 0; r--) {
                    newGrid[r][c] = newGrid[r - 1][c];
                }
                // New top
                let type = 'letter';
                if (Math.random() < STONE_CHANCE) type = 'stone';
                const char = (type === 'letter') ? generateChar(c) : '';
                newGrid[0][c] = createTile(Date.now(), char, type);

                console.log("Stone fell off!");
            }
        }

        setGrid(newGrid);
        setIsProcessing(false);

        // Check Win Condition
        checkWin(newGrid);
    };

    const checkWin = (currentGrid) => {
        const hasStones = currentGrid.flat().some(t => t && t.type === 'stone');
        if (!hasStones) {
            setShowWin(true);
            setMessage("MIND CLEARED!");
            setTimeout(() => {
                setLevel(l => l + 1);
                initializeGrid(false); // Keep score!
            }, 3000);
        }
    };

    // --- Render Helpers ---
    const getTileStyle = (tile, r, c) => {
        const isSelected = selection.some(s => s.r === r && s.c === c);

        let baseClass = "w-full h-full rounded-lg flex items-center justify-center text-2xl font-bold transition-all duration-200 select-none shadow-sm ";

        if (tile.type === 'stone') {
            return baseClass + (tile.hp === 1 ? "bg-slate-600 border-2 border-slate-400 text-transparent" : "bg-slate-700 text-transparent");
        }

        if (tile.type === 'rainbow') {
            return baseClass + (isSelected ? "bg-white ring-4 ring-purple-400" : "bg-gradient-to-br from-purple-400 via-pink-500 to-yellow-500 text-white animate-pulse shadow-[0_0_15px_rgba(236,72,153,0.6)]");
        }

        if (tile.type === 'breakthrough') {
            return baseClass + (isSelected ? "bg-yellow-300 scale-95 ring-4 ring-yellow-500" : "bg-yellow-400 text-yellow-900 animate-pulse shadow-[0_0_15px_rgba(250,204,21,0.6)]");
        }

        // Normal Letter
        if (isSelected) {
            return baseClass + "bg-cyan-500 text-white scale-95 shadow-inner";
        }

        if (tile.isBlinking) {
            return baseClass + "bg-white text-cyan-600 ring-2 ring-cyan-400 animate-pulse";
        }

        if (tile.isAboutToBlink) {
            return baseClass + "bg-orange-100 text-orange-800 animate-pulse ring-2 ring-orange-300";
        }

        return baseClass + "bg-white text-slate-700 hover:bg-slate-50";
    };

    return (
        <div className="w-full h-full flex flex-col items-center bg-slate-100 rounded-xl overflow-hidden relative">
            {/* Header */}
            <div className="w-full bg-white p-4 shadow-sm flex justify-between items-center z-10">
                <div className="flex items-center gap-2">
                    <Trophy className="text-yellow-500" size={20} />
                    <span className="font-mono font-bold text-slate-700 text-xl">{score}</span>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={initializeGrid} className="p-2 hover:bg-slate-100 rounded-full text-slate-500">
                        <RefreshCw size={20} />
                    </button>
                </div>
            </div>

            {/* Message Area */}
            <div className="h-12 flex items-center justify-center w-full bg-slate-50 relative">
                <span className={`font-bold text-lg ${message.includes("!") ? "text-cyan-600 animate-bounce" : "text-slate-400"}`}>
                    {message}
                </span>
                {showWin && (
                    <div className="absolute inset-0 bg-white/90 flex items-center justify-center z-50">
                        <span className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-600 animate-pulse">
                            MIND CLEARED!
                        </span>
                    </div>
                )}
                {isLoadingDictionary && (
                    <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-40">
                        <span className="text-sm font-bold text-slate-400 animate-pulse">
                            Loading Dictionary...
                        </span>
                    </div>
                )}
            </div>

            {/* Grid Container */}
            <div className="flex-1 w-full flex items-center justify-center p-4">
                <div
                    ref={gridRef}
                    className="grid grid-cols-6 gap-2 w-full max-w-md aspect-square bg-slate-200 p-2 rounded-xl shadow-inner touch-none"
                    // Mouse Events
                    onMouseDown={(e) => {
                        const cell = getCellFromTouch(e.clientX, e.clientY);
                        if (cell) handleStart(cell.r, cell.c);
                    }}
                    onMouseMove={(e) => {
                        if (e.buttons === 1) { // Left click held
                            const cell = getCellFromTouch(e.clientX, e.clientY, true);
                            if (cell) handleMove(cell.r, cell.c);
                        }
                    }}
                    onMouseUp={handleEnd}
                    onMouseLeave={handleEnd}

                    // Touch Events
                    onTouchStart={(e) => {
                        // e.preventDefault(); // Handled by CSS touch-action: none usually, but good to be safe if needed
                        const touch = e.touches[0];
                        const cell = getCellFromTouch(touch.clientX, touch.clientY);
                        if (cell) handleStart(cell.r, cell.c);
                    }}
                    onTouchMove={(e) => {
                        // e.preventDefault();
                        const touch = e.touches[0];
                        const cell = getCellFromTouch(touch.clientX, touch.clientY, true);
                        if (cell) handleMove(cell.r, cell.c);
                    }}
                    onTouchEnd={handleEnd}
                >
                    {grid.map((row, r) => (
                        row.map((tile, c) => (
                            <div key={`${r}-${c}`} className="relative aspect-square">
                                {tile && (
                                    <div className={getTileStyle(tile, r, c)}>
                                        {tile.type === 'stone' ? (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <div className="w-3/4 h-3/4 bg-slate-600 rounded-sm opacity-50" />
                                                {tile.hp < 2 && <div className="absolute inset-0 flex items-center justify-center"><div className="w-full h-0.5 bg-slate-800 rotate-45"></div></div>}
                                            </div>
                                        ) : tile.type === 'rainbow' ? (
                                            <div className="flex flex-col items-center">
                                                <Zap size={24} fill="currentColor" className="text-white drop-shadow-md" />
                                                <span className="text-xs font-black text-white drop-shadow-md">{tile.char}</span>
                                            </div>
                                        ) : (
                                            tile.char
                                        )}
                                    </div>
                                )}
                            </div>
                        ))
                    ))}
                </div>
            </div>

            {/* Current Selection Display */}
            <div className="h-16 w-full bg-white border-t border-slate-200 flex items-center justify-center">
                <div className="flex gap-1">
                    {selection.map((s, i) => (
                        <span key={i} className="w-8 h-8 flex items-center justify-center bg-cyan-100 text-cyan-700 font-bold rounded shadow-sm">
                            {grid[s.r][s.c].char}
                        </span>
                    ))}
                    {selection.length === 0 && <span className="text-slate-300 italic text-sm">Swipe tiles...</span>}
                </div>
            </div>
        </div>
    );
};

export default WritersBlockdown;
